import axios from 'axios';
import * as Sentry from '@sentry/react';

import { ReactNode, createContext, useContext, useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useUserData } from '@/hooks/useUserData/useUserData';
import { queryKeys } from '@/lib/queryKeys';
import { setTrackingContext } from '@/modules/Analytics';
import { userData } from '@/types/types';
import { API_URL } from '@/lib/apiUrl';

interface AuthContextType {
  userData: userData | null;
  isSignedIn: boolean;
  loading: boolean;
  refetchUserData: () => Promise<void>;
  logout: () => void;
  login: (email: string, password: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const { data, isLoading, refetch } = useUserData();
  const userData = data ?? null;

  useEffect(() => {
    if (userData) {
      Sentry.setUser({ id: String(userData.id) });
    } else {
      Sentry.setUser(null);
    }

    setTrackingContext(userData);
  }, [userData]);

  const login = async (email: string, password: string) => {
    await axios.post(
      `${API_URL}/login`,
      { email, password },
      { withCredentials: true }
    );

    await refetch();
  };

  const logout = () => {
    queryClient.removeQueries({
      queryKey: queryKeys.userData.current.queryKey,
    });
    // Use window.location.href for logout to ensure clean slate
    window.location.href = '/experiences/login';
  };

  return (
    <AuthContext.Provider
      value={{
        userData,
        isSignedIn: !isLoading && !!userData,
        loading: isLoading,
        refetchUserData: async () => {
          await refetch();
        },
        logout,
        login,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
